/**
 * Retrocompatibilidade do `download_registers` — a tabela que dizia **quais arquivos já tinham sido
 * baixados**.
 *
 * O app anterior guardava essa lista numa store do `BATE_FORTE`; o atual guarda a mesma informação
 * no `__meta` do `BF_SELLER_DATASETS`, gravada arquivo a arquivo pelo sync. São o mesmo dado em
 * lugares diferentes, e é por isso que ele precisa atravessar a renomeação junto com o resto.
 *
 * O sintoma de não migrar aparece nas imagens. Depois da atualização a fase pesada copia o catálogo
 * de imagens para o `BF_SELLER_IMAGES`, então `temDado('imagens')` responde `true` — mas o `__meta`
 * nasceu vazio, `foiBaixado('imagens', …)` responde `false`, e o `_useGetLinks` (ver a condição
 * `!isFilledOnDB || !isRegisteredSuccess`) manda baixar o zip de novo. O RCA paga centenas de MB de
 * rede móvel para receber exatamente as imagens que já estão no aparelho.
 *
 * ## Por que só as imagens são semeadas
 *
 * `buildDownloadState` faz `comDado.add(entry.tipo)` para toda entrada `ok` do `__meta`, e é o
 * `comDado` que responde `temDado(tipo)` de **todo dataset que não é imagem**. Semear `catalogo` ou
 * `preco` a partir do registro antigo diria "esse dataset já tem conteúdo" sobre um banco que a
 * migração acabou de apagar (`dropLegacyDatasetsDb`, de propósito: é dado de servidor). O app
 * pularia o download e ficaria com catálogo vazio, sem nada para corrigi-lo no sync seguinte.
 *
 * As imagens escapam dessa armadilha porque `temDado` as trata à parte — quem responde por elas é a
 * contagem no `BF_SELLER_IMAGES`, não o `comDado`. Semear imagem mexe só no `foiBaixado`, e só
 * depois de as imagens estarem mesmo copiadas. Os demais registros são lidos e descartados.
 */

import { toCoded } from '@/shared/coded';
import { isImageDatasetType, writeImageDatasetFile } from '@/shared/bfSeller';
import { readImageDatasets } from '@/shared/imageDatasetsStorage';
import { getOnStorage } from '@/shared/storageFunctions';
import { openImagesDBConnection } from '@/talons/dbFunctions/useDBFunctions';
import { normalizeDatasetType, urlSemAutenticacao } from '@/talons/global-downloadFiles/_syncCommons';
import type { DataSetsProps } from '@/types';
import { LEGACY_DATASETS_DB, openLegacyDbIfExists } from './legacyIdbNames';

/**
 * Onde os registros de imagem esperam entre a captura e a semeadura.
 *
 * As duas pontas não cabem no mesmo instante: a tabela mora no banco de datasets, que é apagado
 * cedo para liberar espaço, e o registro só é verdade depois de as imagens estarem copiadas — o que
 * pode levar vários boots, em lotes. Guardar no `localStorage` é o que faz a informação atravessar
 * esse intervalo; só as linhas de imagem entram, então são poucas centenas de bytes.
 */
const STASH_KEY = 'bf_seller_legacy_download_registers';

/** A store do `BATE_FORTE` onde a lista vivia. */
const LEGACY_REGISTERS_STORE = toCoded('download_registers');

/** As duas stores que `validateTable('imagens')` soma para dizer que há imagem no aparelho. */
const STORE_IMAGENS = toCoded('imagens');
const STORE_IMAGES_ZIP = toCoded('images_zip');

/** Uma linha do `download_registers` antigo. Nunca teve `DT_UPDATE` — ver `descritoresPorUrl`. */
export type LegacyDownloadRegister = {
  url: string;
  TIPO_ARQUIVO: string;
  downloadedAt?: string;
};

const ehRegistroUtil = (item: unknown): item is LegacyDownloadRegister => {
  const linha = item as Partial<LegacyDownloadRegister> & { tipoArquivo?: string };
  return !!linha?.url && !!(linha?.TIPO_ARQUIVO || linha?.tipoArquivo);
};

const normalizarRegistro = (item: LegacyDownloadRegister & { tipoArquivo?: string }): LegacyDownloadRegister => ({
  url: item.url,
  TIPO_ARQUIVO: item.TIPO_ARQUIVO || (item.tipoArquivo as string),
  downloadedAt: item.downloadedAt,
});

/**
 * Lê o `download_registers` do banco antigo.
 *
 * O formato é o de todas as tabelas daquele banco: a store tem `keyPath` embutido e
 * `autoIncrement`, e cada gravação empilha um registro `{ data: [...] }` novo. Quem lia pegava
 * sempre o **último** — os anteriores são versões superadas da mesma lista. Reproduzir isso é o
 * ponto: pegar o primeiro, ou concatenar todos, ressuscitaria registro que o app antigo já tinha
 * descartado (expirado ou removido por `removeDownloadRegisters`).
 *
 * Devolve `[]` quando o banco ou a store não existem — é o caso comum de instalação nova.
 */
export const readLegacyDownloadRegisters = async (): Promise<LegacyDownloadRegister[]> => {
  const db = await openLegacyDbIfExists(LEGACY_DATASETS_DB);
  if (!db) return [];

  try {
    if (!db.objectStoreNames.contains(LEGACY_REGISTERS_STORE)) return [];

    const tx = db.transaction(LEGACY_REGISTERS_STORE, 'readonly');
    const registros = await tx.objectStore(LEGACY_REGISTERS_STORE).getAll();
    await tx.done;

    const ultimo = registros[registros.length - 1];
    const lista = (ultimo as { data?: unknown })?.data;
    if (!Array.isArray(lista)) return [];

    return lista.filter(ehRegistroUtil).map(normalizarRegistro);
  } catch (error) {
    console.info('[legacyIdb] não foi possível ler o download_registers antigo:', error);
    return [];
  } finally {
    db.close();
  }
};

/**
 * Descritores de dataset conhecidos, indexados pela URL sem assinatura.
 *
 * É daqui que sai o `DT_UPDATE` que o registro antigo não guardava — e sem ele não há semeadura
 * possível: `identidadeArquivo` põe o `DT_UPDATE` na identidade justamente porque o arquivo é
 * republicado no mesmo caminho, então uma entrada sem ele nunca casaria com a lista que o
 * `getdataset` devolve.
 *
 * Os descritores ficam no `localStorage` (`datasets` e `datasets_imagens`), que a renomeação dos
 * bancos não tocou. A leitura precisa acontecer **antes do primeiro sync**: o `_useGetLinks`
 * sobrescreve `datasets` com a lista nova logo no começo.
 */
const descritoresPorUrl = (): Map<string, DataSetsProps> => {
  const out = new Map<string, DataSetsProps>();

  const listas: DataSetsProps[][] = [];
  try {
    listas.push(getOnStorage('datasets') || []);
  } catch {
    /* storage indisponível */
  }
  try {
    listas.push(readImageDatasets());
  } catch {
    /* idem */
  }

  for (const dataset of listas.flat()) {
    if (!dataset?.URL_S3) continue;
    out.set(urlSemAutenticacao(dataset.URL_S3), dataset);
  }

  return out;
};

/**
 * Quantas imagens o banco novo tem, somando as mesmas duas stores que `validateTable('imagens')`.
 *
 * Serve de dois jeitos: é a prova de que a cópia aconteceu (sem ela não há o que registrar) e é o
 * `rows` da entrada do `__meta`, que `buildDownloadState` exige maior que zero.
 */
const contarImagensNoBancoNovo = async (): Promise<number> => {
  let db;
  try {
    db = await openImagesDBConnection();
    const stores = [STORE_IMAGENS, STORE_IMAGES_ZIP].filter((store) => db!.objectStoreNames.contains(store));
    if (!stores.length) return 0;

    const tx = db.transaction(stores, 'readonly');
    const totais = await Promise.all(stores.map((store) => tx.objectStore(store).count()));
    await tx.done;

    return totais.reduce((soma, total) => soma + total, 0);
  } catch (error) {
    console.info('[legacyIdb] não foi possível contar as imagens do banco novo:', error);
    return 0;
  } finally {
    db?.close();
  }
};

/**
 * Lê o `download_registers` antigo e guarda as linhas de imagem no `localStorage`.
 *
 * Chamar **antes** de `dropLegacyDatasetsDb` — depois dele a tabela não existe mais. Guardar `[]`
 * é resultado legítimo (aparelho sem nada a migrar) e vale como "já capturado": quem controla a
 * repetição é o marcador de fase, não a presença do stash.
 *
 * @returns quantos registros de imagem foram guardados.
 */
export const captureLegacyDownloadRegisters = async (): Promise<number> => {
  const registros = await readLegacyDownloadRegisters();
  const deImagem = registros.filter((registro) => isImageDatasetType(normalizeDatasetType(registro.TIPO_ARQUIVO)));

  try {
    localStorage.setItem(STASH_KEY, JSON.stringify(deImagem));
  } catch {
    /* storage cheio/bloqueado: sem stash, o zip desce de novo — caro, mas não é perda de dado */
  }

  return deImagem.length;
};

const lerStash = (): LegacyDownloadRegister[] => {
  try {
    const bruto = localStorage.getItem(STASH_KEY);
    if (!bruto) return [];
    const lista = JSON.parse(bruto);
    return Array.isArray(lista) ? lista.filter(ehRegistroUtil).map(normalizarRegistro) : [];
  } catch {
    return [];
  }
};

export const clearStashedDownloadRegisters = (): void => {
  try {
    localStorage.removeItem(STASH_KEY);
  } catch {
    /* idem */
  }
};

/**
 * Escreve no `__meta` os arquivos de imagem que o registro antigo dá como baixados.
 *
 * Roda **depois** de `migrateLegacyImages` ter concluído: o registro só é verdade se as imagens
 * estiverem no `BF_SELLER_IMAGES`, e é a contagem de lá que serve de `rows`. Um arquivo cujo
 * descritor não apareceu no `localStorage` é pulado — sem `DT_UPDATE` a entrada nasceria incapaz de
 * casar com a lista do `getdataset`, e registro que nunca casa é só lixo no banco.
 *
 * @returns quantos arquivos de imagem foram registrados.
 */
export const seedImageDatasetMetaFromStash = async (): Promise<number> => {
  const registros = lerStash();
  if (!registros.length) {
    clearStashedDownloadRegisters();
    return 0;
  }

  const rows = await contarImagensNoBancoNovo();
  if (!rows) {
    clearStashedDownloadRegisters();
    return 0;
  }

  const descritores = descritoresPorUrl();
  let semeados = 0;

  for (const registro of registros) {
    const descritor = descritores.get(urlSemAutenticacao(registro.url));
    if (!descritor) continue;

    // `rows` é o total do aparelho, não o deste arquivo: o registro antigo não guardava a contagem
    // por zip. Só o "maior que zero" é lido, e o próximo sync que reescrever o arquivo corrige.
    await writeImageDatasetFile({
      tipo: normalizeDatasetType(registro.TIPO_ARQUIVO),
      dataset: { ...descritor, URL_S3: descritor.URL_S3, DT_UPDATE: descritor.DT_UPDATE },
      rows,
    });
    semeados += 1;
  }

  clearStashedDownloadRegisters();

  if (semeados) console.info(`[legacyIdb] ${semeados} arquivo(s) de imagem registrados a partir do download_registers`);
  return semeados;
};
